import { readCampusResourceAdvisory, campusResourceAdvisoryRecommendations } from './campusResourceAdvisory';
import type { BopHeatRejection, Recommendation, SiteRagReport } from '../../types/siteRag';
import { analysisReadinessDecisionV1Schema } from '../../types/decisionAnalysis';
import type { BtmStrategy } from '../stage2/stage2Types';
import type { PowerOptionId } from '../stage2/stage2Evidence';
import { NUMBERED_REF_TOKEN_RE } from '../citationTokens';
import { reportCanContributeSavedResult } from '../pathToGo/reportPlan';

export type ScreeningArchitectureLineId =
  | 'btm-architecture' | 'power-type' | 'dc-power' | 'cooling-strategy' | 'cooling-type';

export const SCREENING_ARCHITECTURE_LINE_IDS: readonly ScreeningArchitectureLineId[] = [
  'btm-architecture',
  'power-type',
  'dc-power',
  'cooling-strategy',
  'cooling-type',
];

export type ScreeningQuestionStatusV1 =
  | 'recommended'
  | 'alternatives'
  | 'unresolved'
  | 'not-produced'
  | 'withheld-source';

export type ScreeningSourceDiagnosticState =
  | 'ready'
  | 'missing-report'
  | 'identity-mismatch'
  | 'metadata-only'
  | 'draft-report'
  | 'wrong-report-type'
  | 'quality-hold'
  | 'readiness-blocked';

/** A saved advisory output, never an accepted Intent choice or an engineering result. */
export interface ScreeningArchitectureRecommendation {
  value: string;
  basisNote: string;
  reportId: string;
  fieldPath: string;
  citations: string[];
  strategy?: BtmStrategy;
  /**
   * The saved text explicitly says islanded ("islanded power|supply|generation").
   * 'BTM-only' / 'behind-the-meter only' is a meter arrangement, not an
   * islanding declaration, and never sets this.
   */
  islanded?: true;
  supplyOptions?: PowerOptionId[];
  coolingStrategy?: 'air' | 'liquid' | 'hybrid';
  heatRejection?: Exclude<BopHeatRejection, 'unknown'>;
  spoken?: string;
}

export interface ScreeningQuestionAssessmentV1 {
  questionId: ScreeningArchitectureLineId;
  status: ScreeningQuestionStatusV1;
  value?: string;
  alternatives?: string[];
  basisNote: string;
  unresolvedReason?: string;
  nextStep?: string;
  reportId: string;
  fieldPath: string;
  citations: string[];
  strategy?: BtmStrategy;
  islanded?: true;
  supplyOptions?: PowerOptionId[];
  coolingStrategy?: 'air' | 'liquid' | 'hybrid';
  heatRejection?: Exclude<BopHeatRejection, 'unknown'>;
  spoken?: string;
}

export interface ScreeningRecommendationSetV1 {
  schemaVersion: 'screening-recommendation-set-v1';
  reportId: string;
  screeningMode: 'recommend' | 'evaluate-proposal';
  sourceState: ScreeningSourceDiagnosticState;
  sourceReason?: string;
  questions: Record<ScreeningArchitectureLineId, ScreeningQuestionAssessmentV1>;
}

export type ScreeningArchitectureRecommendations =
  Partial<Record<ScreeningArchitectureLineId, ScreeningArchitectureRecommendation>>;
export type ScreeningRecommendationReport = Omit<SiteRagReport, 'decisionAnalysis'> & {
  screeningRecommendationSetV1?: ScreeningRecommendationSetV1;
};

const text = (value: unknown): string => typeof value === 'string' ? value.trim() : '';
const numberedCitations = (value: string): string[] =>
  [...new Set(value.match(new RegExp(NUMBERED_REF_TOKEN_RE.source, NUMBERED_REF_TOKEN_RE.flags)) ?? [])];
const withoutCitations = (value: string): string => value
  .replace(new RegExp(NUMBERED_REF_TOKEN_RE.source, NUMBERED_REF_TOKEN_RE.flags), '')
  .trim().replace(/[.;]+$/, '').trim();
const hasAdvisoryValue = (value: unknown): boolean => {
  const clean = withoutCitations(text(value));
  return Boolean(clean) && !/^(?:unknown|undecided|unavailable|tbd|not (?:yet )?(?:determined|assessed|provided|recorded))(?:$|\s*(?:[—–:;.]|pending\b))/i.test(clean);
};

/** This does not replace the caller's currentness/owner/body-loading gates. */
export function diagnoseScreeningSourceState(
  report: ScreeningRecommendationReport | null | undefined,
  siteId?: string,
): { state: ScreeningSourceDiagnosticState; reason?: string } {
  if (!report) {
    return { state: 'missing-report', reason: 'No completed Screening Assessment report is saved for this site.' };
  }
  if (siteId != null && report.siteId != null && report.siteId !== siteId) {
    return { state: 'identity-mismatch', reason: 'The candidate report belongs to a different site identity.' };
  }
  if (report._metadataOnly === true) {
    return { state: 'metadata-only', reason: 'Only report metadata is loaded; open or refresh Screening Assessment to load the full report body.' };
  }
  if (report.generationTier === 'draft' || report.isDraft === true) {
    return { state: 'draft-report', reason: 'The saved Screening Assessment is a draft preview and cannot supply governed recommendations.' };
  }
  if (report.qualityHold === 'needs-revision' || (report.qualityHoldFindings?.length ?? 0) > 0) {
    return { state: 'quality-hold', reason: 'The saved Screening Assessment is held for quality/narrative revision and is withheld from recommendation promotion.' };
  }
  if (!reportCanContributeSavedResult(report, 'feasibility')) {
    return { state: 'wrong-report-type', reason: 'The saved report does not have a valid immutable feasibility revision.' };
  }
  const raw = report.analysisReadiness ?? report.analysisRevision?.readiness;
  if (raw == null) return { state: 'ready' };
  const parsed = analysisReadinessDecisionV1Schema.safeParse(raw);
  const ok = parsed.success && parsed.data.canView && parsed.data.identityValid
    && parsed.data.qualityReady && parsed.data.narrativeReady
    && ((parsed.data.ready && parsed.data.canUseForDecision)
      || parsed.data.state === 'queued' || parsed.data.state === 'refreshing');
  return ok
    ? { state: 'ready' }
    : { state: 'readiness-blocked', reason: 'The saved Screening Assessment readiness gate is not cleared for decision use.' };
}

function sourceCanSupplyAdvisory(report: ScreeningRecommendationReport, siteId?: string): boolean {
  return diagnoseScreeningSourceState(report, siteId).state === 'ready';
}

function savedLine(
  report: ScreeningRecommendationReport,
  value: string,
  fieldPath: string,
  qualification: string,
  semantic: Partial<ScreeningArchitectureRecommendation> = {},
): ScreeningArchitectureRecommendation {
  const basisNote = ['Saved screening recommendation; review before adopting', qualification].filter(Boolean).join('. ');
  return { ...semantic, value, basisNote, reportId: report.id, fieldPath,
    citations: numberedCitations(`${value} ${qualification}`) };
}

function recommendationQualification(row: Recommendation): string {
  // Both the description and rationale can carry a limiting condition. Neither
  // is shortened, reworded, stripped of citations or promoted to source truth.
  return [...new Set([text(row.description), text(row.rationale)].filter(Boolean))].join(' ');
}

/** Closed noun phrases only. An incidental technology word in prose is not a selection. */
function supplyOption(value: string): PowerOptionId | undefined {
  const clean = withoutCitations(value).toLowerCase();
  if (/^(?:utility )?grid(?: import| supply| power)?$/.test(clean)) return 'grid';
  if (/^(?:(?:natural[- ]gas|gas)[- ](?:fired )?)?(?:reciprocating|recip) (?:gas )?engines?$/.test(clean)) return 'recip';
  if (/^(?:natural[- ]gas |gas[- ]fired )?combined[- ]cycle(?: gas)?(?: blocks?| turbines?)?$/.test(clean)) return 'gas-cc';
  if (/^(?:natural[- ]gas |gas[- ]fired )?simple[- ]cycle(?: gas)?(?: turbines?)?$/.test(clean)) return 'gas-sc';
  if (/^solar(?: pv)? (?:and|with) (?:bess|battery storage)$/.test(clean)) return 'solar-bess';
  if (/^wind(?: turbines?)? (?:and|with) (?:bess|battery storage)$/.test(clean)) return 'wind-bess';
  if (/^(?:smr|small modular reactors?)$/.test(clean)) return 'smr';
  if (/^(?:large nuclear|large nuclear reactors?)$/.test(clean)) return 'large-nuclear';
  return undefined;
}

function supplySemantics(value: string): Partial<ScreeningArchitectureRecommendation> {
  const clean = withoutCitations(value);
  const singleOption = supplyOption(clean);
  const parts = clean.split(/\s+(?:\+|and|with)\s+/i);
  const options = parts.map(supplyOption);
  const supplyOptions = singleOption ? [singleOption]
    : options.length && options.every((option): option is PowerOptionId => option != null)
      ? [...new Set(options)] : undefined;
  // Naming on-site technology says nothing about the grid connection. Even a
  // closed technology list cannot manufacture islanded/BTM-only architecture.
  const explicit = clean.toLowerCase();
  const islanded = /^(?:islanded|off[- ]grid)(?: (?:power|supply|generation))?$/.test(explicit);
  const strategy: BtmStrategy | undefined = /^(?:grid[- ]only|utility grid only)$/.test(explicit)
    ? 'grid-only'
    : islanded
      ? 'islanded'
      : /^(?:btm[- ]only|behind[- ]the[- ]meter only)$/.test(explicit)
        ? 'btm-only'
        : /^(?:hybrid(?: grid and (?:on[- ]site|btm)(?: supply| generation)?)?|grid (?:import )?(?:and|\+) (?:on[- ]site|btm) (?:supply|generation))$/.test(explicit)
          ? 'hybrid' : undefined;
  return {
    ...(supplyOptions ? { supplyOptions } : {}),
    ...(strategy ? { strategy } : {}),
    ...(islanded ? { islanded: true as const } : {}),
  };
}

function coolingLoop(value: string): 'air' | 'liquid' | 'hybrid' | undefined {
  const clean = value.toLowerCase().trim();
  if (/^(?:air|air[- ]cooling|air[- ]cooled racks?)$/.test(clean)) return 'air';
  if (/^(?:(?:direct[- ])?liquid(?: (?:rack )?cooling)?|direct[- ]to[- ]chip(?: liquid)?(?: cooling)?)$/.test(clean)) return 'liquid';
  if (/^hybrid(?: (?:(?:air and liquid|rack) )?cooling)?$/.test(clean)) return 'hybrid';
  return undefined;
}

function coolingSink(value: string): Exclude<BopHeatRejection, 'unknown'> | undefined {
  const clean = value.toLowerCase().trim();
  if (/^(?:dry coolers?|dry[- ]cooling|dry heat rejection)$/.test(clean)) return 'dry-cooler';
  if (/^(?:wet(?: cooling)? towers?|wet[- ]cooling(?: towers?)?)$/.test(clean)) return 'wet-cooling-tower';
  if (/^(?:hybrid(?: heat rejection| coolers?| cooling towers?)|adiabatic(?:[- ]assist)?(?: coolers?| cooling)?)$/.test(clean)) return 'hybrid';
  if (/^once[- ]through(?: cooling| heat rejection)?$/.test(clean)) return 'once-through';
  if (/^absorption[- ]cchp$/.test(clean)) return 'absorption-cchp';
  if (/^free[- ]cooling$/.test(clean)) return 'free-cooling';
  return undefined;
}

function coolingSemantics(value: string): Partial<ScreeningArchitectureRecommendation> {
  const clean = withoutCitations(value);
  const parts = clean.split(/\s+(?:with|and|\+)\s+/i);
  if (parts.length === 2) {
    const coolingStrategy = coolingLoop(parts[0]!);
    const heatRejection = coolingSink(parts[1]!);
    if (coolingStrategy && heatRejection) return { coolingStrategy, heatRejection };
  }
  const coolingStrategy = coolingLoop(clean);
  const heatRejection = coolingSink(clean);
  return { ...(coolingStrategy ? { coolingStrategy } : {}), ...(heatRejection ? { heatRejection } : {}) };
}

const RECOMMENDED_RANGE_LABEL = /^(?:recommended screening (?:size|capacity|load) range|screening[- ]recommended (?:size|capacity|load) range)$/i;
const NUMBER = '(?:\\d{1,3}(?:,\\d{3})+|\\d+)(?:\\.\\d+)?';
const MW_RANGE = new RegExp(`^(${NUMBER})\\s*(?:[–—-]|to)\\s*(${NUMBER})\\s*MW\\s+(?:of\\s+)?(?:IT(?:\\s+load|\\s+demand)?|facility(?:\\s+load|\\s+demand)?)\\b`, 'i');

function alternatives(
  report: ScreeningRecommendationReport,
  lines: ScreeningArchitectureRecommendation[],
): ScreeningArchitectureRecommendation | undefined {
  const distinct = lines.filter((line, index) => lines.findIndex(other => other.value === line.value) === index);
  if (distinct.length === 0) return undefined;
  if (distinct.length === 1 && lines.length === 1) return distinct[0];
  if (distinct.length === 1) return savedLine(report, distinct[0]!.value,
    lines.map(line => line.fieldPath).join('; '), lines.map(line => line.basisNote).join(' '), distinct[0]);
  // No arbitrary first-match choice and no scalar metadata for a conflicting
  // shortlist. Every saved condition and citation is retained for review.
  return savedLine(report, `Screening alternatives: ${distinct.map(line => line.value).join('; ')}`,
    lines.map(line => line.fieldPath).join('; '), lines.map(line => line.basisNote).join(' '));
}

function explicitSizeRange(row: Recommendation): string | null {
  // capacityMW in developmentPathway is DECLARED cumulative demand. Water and
  // economic ranges are what-if inputs. Only a positively labeled saved sizing
  // recommendation can enter this advisory tier; no arithmetic occurs here.
  const original = text(row.description) || text(row.architecture);
  let proof = withoutCitations(original);
  if (!RECOMMENDED_RANGE_LABEL.test(withoutCitations(text(row.title)))) {
    const label = /^(?:Recommended screening (?:size|capacity|load) range|Screening[- ]recommended (?:size|capacity|load) range)\s*:\s*/i.exec(proof);
    if (!label) return null;
    proof = proof.slice(label[0].length);
  } else {
    proof = proof.replace(/^(?:Recommended screening (?:size|capacity|load) range|Screening[- ]recommended (?:size|capacity|load) range)\s*:\s*/i, '');
  }
  const range = MW_RANGE.exec(proof);
  if (!range || /(?:what[- ]if|illustrative|declared (?:demand|load|target)|customer (?:demand|load|target)|not recommended|do not (?:use|adopt)|no recommended)/i.test(original)) return null;
  const low = Number(range[1]!.replaceAll(',', ''));
  const high = Number(range[2]!.replaceAll(',', ''));
  return Number.isFinite(low) && low > 0 && Number.isFinite(high) && high > low ? original : null;
}

/** Speech is a qualified label, not a truncated saved paragraph. The full
 * display value, basis and citations above remain the reviewable authority. */
function compactSpoken(id: ScreeningArchitectureLineId, line: ScreeningArchitectureRecommendation): string {
  if (id === 'dc-power') {
    const proof = withoutCitations(line.value).replace(/^(?:Recommended screening (?:size|capacity|load) range|Screening[- ]recommended (?:size|capacity|load) range)\s*:\s*/i, '');
    const range = MW_RANGE.exec(proof);
    if (range) return `a conditional ${range[1]}–${range[2]} MW ${/\bIT\b/i.test(range[0]) ? 'IT-load' : 'facility-demand'} range`;
    return 'a conditional DC size-range shortlist requiring review';
  }
  if (id === 'btm-architecture') {
    if (line.strategy === 'grid-only') return 'a conditional grid-only supply option';
    if (line.strategy === 'btm-only') return 'a conditional BTM-only supply option';
    if (line.strategy === 'hybrid') return 'a conditional hybrid grid and BTM supply option';
    if (line.strategy === 'islanded') return 'a conditional islanded (off-grid) supply option';
    if (line.supplyOptions?.length === 1 && line.supplyOptions[0] === 'grid') return 'a conditional grid-supply option, with connection capacity unconfirmed';
    return 'a conditional BTM supply shortlist, with grid connection unconfirmed';
  }
  if (id === 'power-type') {
    const labels: Record<PowerOptionId, string> = {
      grid: 'grid supply', recip: 'reciprocating gas engines', 'gas-cc': 'combined-cycle gas supply',
      'gas-sc': 'simple-cycle gas supply', 'solar-bess': 'solar with battery storage',
      'wind-bess': 'wind with battery storage', smr: 'small modular reactors', 'large-nuclear': 'large nuclear',
    };
    return line.supplyOptions?.length === 1 ? `a conditional ${labels[line.supplyOptions[0]!]} option`
      : 'a conditional power-supply shortlist requiring review';
  }
  if (id === 'cooling-strategy') return line.coolingStrategy
    ? `conditional ${line.coolingStrategy} rack cooling` : 'a conditional rack-cooling shortlist requiring review';
  const sinks: Record<Exclude<BopHeatRejection, 'unknown'>, string> = {
    'dry-cooler': 'dry heat rejection', 'wet-cooling-tower': 'wet-tower heat rejection', hybrid: 'hybrid heat rejection',
    'once-through': 'once-through heat rejection', 'absorption-cchp': 'absorption CCHP', 'free-cooling': 'free cooling',
  };
  return line.heatRejection ? `conditional ${sinks[line.heatRejection]}` : 'a conditional heat-rejection shortlist requiring review';
}

/**
 * Reviewed compatibility adapter for producer-shaped power recommendation rows
 * where `row.architecture` was omitted by the model, but `row.title` / `row.description`
 * states an affirmative, source-qualified architecture choice (e.g. "Proceed with
 * the declared Phase 1 ... grid-led architecture ..."). Never extracts from
 * negations or conditional "if" hypotheses.
 */
function adaptProducerPowerArchitecture(
  report: ScreeningRecommendationReport,
  powerRows: ReadonlyArray<{ row: Recommendation; index: number }>,
): ScreeningArchitectureRecommendation | undefined {
  const adapted: ScreeningArchitectureRecommendation[] = [];
  for (const { row, index } of powerRows) {
    if (hasAdvisoryValue(row.architecture) || RECOMMENDED_RANGE_LABEL.test(withoutCitations(text(row.title)))) continue;
    const desc = text(row.description);
    if (!hasAdvisoryValue(desc)) continue;
    const cleanDesc = withoutCitations(desc);
    const combined = `${withoutCitations(text(row.title))} ${cleanDesc}`;
    if (
      /\b(?:not recommended|do not (?:use|select|adopt)|disqualified|not applicable)\b/i.test(combined)
      || /^if\b/i.test(cleanDesc)
      || !/\b(?:proceed with|retain|adopt|use|implement|deploy|select|pursue|execute)\b/i.test(combined)
    ) {
      continue;
    }
    let semantic: Partial<ScreeningArchitectureRecommendation> | undefined;
    if (/\b(?:grid[- ]led\s+(?:architecture|supply|power|equipment\s+release)|grid[- ]only\s+(?:architecture|supply|power)|utility[- ]grid[- ]only)\b/i.test(combined)) {
      semantic = { strategy: 'grid-only', supplyOptions: ['grid'] };
    } else if (/\bhybrid\s+grid\s+(?:and|\+|\/)\s+(?:on[- ]site|btm)\s+(?:architecture|supply|generation|power)\b/i.test(combined)) {
      semantic = { strategy: 'hybrid' };
    } else if (/\b(?:btm[- ]only|behind[- ]the[- ]meter[- ]only)\s+(?:architecture|supply|generation|power)\b/i.test(combined)) {
      semantic = { strategy: 'btm-only' };
    } else if (/\b(?:islanded|off[- ]grid)\s+(?:architecture|supply|generation|power)\b/i.test(combined)) {
      semantic = { strategy: 'islanded', islanded: true as const };
    }
    if (semantic) {
      adapted.push(savedLine(report, desc, `recommendations[${index}].description`, recommendationQualification(row), semantic));
    }
  }
  return alternatives(report, adapted);
}

/**
 * Reviewed compatibility adapter for producer-shaped cooling recommendation rows
 * where `row.architecture` is absent and `row.title` is an action heading rather
 * than "Rack cooling" / "Heat rejection", while `row.description` states an
 * affirmative rack-cooling loop and/or heat-rejection sink (e.g. "Retain the
 * declared hybrid air/liquid rack cooling loop ... and dry-cooler heat rejection ...").
 */
function adaptProducerCoolingSemantics(row: Recommendation): Partial<ScreeningArchitectureRecommendation> {
  if (hasAdvisoryValue(row.architecture)) return {};
  const desc = withoutCitations(text(row.description));
  if (!desc) return {};
  const combined = `${withoutCitations(text(row.title))} ${desc}`;
  if (
    /\b(?:not recommended|do not (?:use|select|adopt)|disqualified|not applicable|unknown|undecided|not (?:yet )?determined)\b/i.test(desc)
    || /^if\b/i.test(desc)
    || !/\b(?:retain|adopt|use|implement|deploy|proceed with|select|maintain|configure|design for|recommend)\b/i.test(combined)
  ) {
    return {};
  }
  const coolingStrategy: 'air' | 'liquid' | 'hybrid' | undefined =
    /\bhybrid\s+air(?:\s*\/|\s+and\s+)liquid\s+(?:rack[- ]cooling|cooling)(?:\s+loop)?\b/i.test(combined)
      ? 'hybrid'
      : /\b(?:direct(?:-to-chip)?\s+liquid|liquid)\s+rack[- ]cooling(?:\s+loop)?\b/i.test(combined)
        ? 'liquid'
        : /\bair(?:-cooled)?\s+rack[- ]cooling(?:\s+loop)?\b/i.test(combined)
          ? 'air'
          : undefined;
  const heatRejection: Exclude<BopHeatRejection, 'unknown'> | undefined =
    /\b(?:dry[- ]cooler\s+heat[- ]rejection|dry\s+heat[- ]rejection|dry[- ]fluid\s+coolers?|dry[- ]coolers?)\b/i.test(combined)
      ? 'dry-cooler'
      : /\b(?:wet[- ]cooling\s+towers?|wet\s+cooling\s+towers?|evaporative\s+cooling\s+towers?)\b/i.test(combined)
        ? 'wet-cooling-tower'
        : /\b(?:hybrid\s+heat[- ]rejection|adiabatic[- ]assist(?:ed)?\s+(?:dry\s+)?coolers?|adiabatic\s+heat[- ]rejection|waterside\s+economizers?)\b/i.test(combined)
          ? 'hybrid'
          : /\bfree[- ]cooling\s+heat[- ]rejection\b/i.test(combined)
            ? 'free-cooling'
            : /\babsorption[- ]cchp\s+heat[- ]rejection\b/i.test(combined)
              ? 'absorption-cchp'
              : /\bonce[- ]through\s+(?:cooling|heat[- ]rejection)\b/i.test(combined)
                ? 'once-through'
                : undefined;
  return {
    ...(coolingStrategy ? { coolingStrategy } : {}),
    ...(heatRejection ? { heatRejection } : {}),
  };
}

const BARE_UNRESOLVED_PLACEHOLDER =
  /^(?:unknown|tbd|to be determined|n\/a|na|none|null|undefined|not[-\s]?available|not(?: yet)? determined|undecided|pending|unresolved|unverified|indeterminate)[.!;,\s]*$/i;

const UNRESOLVED_SIGNAL =
  /\b(?:unresolved|unverified|indeterminate|unknown|undecided|not\s+(?:yet\s+)?determined|cannot\s+(?:yet\s+)?be\s+(?:selected|determined|established|fixed|confirmed)|pending\b|deferred\s+pending|insufficient\b|requires?\s+[\w\s-]+\s+(?:verification|confirmation|survey|study|allocation))\b/i;

function isSubstantiveUnresolvedExplanation(value: string | undefined): boolean {
  const clean = withoutCitations(text(value))
    .replace(
      /^(?:Recommended screening (?:size|capacity|load) range|Screening[- ]recommended (?:size|capacity|load) range)\s*:\s*/i,
      '',
    )
    .trim();
  if (!clean || BARE_UNRESOLVED_PLACEHOLDER.test(clean)) return false;
  if (!UNRESOLVED_SIGNAL.test(clean)) return false;
  const body = clean
    .replace(
      /^(?:unknown|tbd|to be determined|not(?: yet)? determined|undecided|pending|unresolved|unverified|indeterminate)\s*[:—–,-]\s*/i,
      '',
    )
    .trim();
  if (!body || BARE_UNRESOLVED_PLACEHOLDER.test(body)) return false;
  const words = body.split(/\s+/).filter(Boolean);
  return body.length >= 15 && words.length >= 3;
}

/**
 * Extract an explicit saved unresolved reason for a screening question when the
 * report investigated the question and recorded why a positive choice cannot yet
 * be made (for example, River Ridge's unresolved sizing explanation or an
 * unresolved campus-resource screening status).
 */
export function deriveUnresolvedScreeningReason(
  report: ScreeningRecommendationReport | null | undefined,
  id: ScreeningArchitectureLineId,
): { reason: string; fieldPath: string; citations: string[]; nextStep?: string } | null {
  if (!report) return null;
  const recommendations = report.recommendations ?? [];
  if (id === 'dc-power') {
    for (const [index, row] of recommendations.entries()) {
      if (row.type !== 'site' && row.type !== 'power') continue;
      if (explicitSizeRange(row) != null) continue;
      const desc = text(row.description);
      const title = text(row.title);
      const clauseMatch = /(?:Recommended screening (?:size|capacity|load) range|Screening[- ]recommended (?:size|capacity|load) range)\s*:\s*([\s\S]+)$/i.exec(desc);
      if (clauseMatch && /\b(?:unresolved|unverified|indeterminate|unknown|pending)\b/i.test(clauseMatch[1]!)) {
        const reasonText = [clauseMatch[0].trim(), text(row.rationale)].filter(Boolean).join(' ');
        if (!isSubstantiveUnresolvedExplanation(clauseMatch[1]!) && !isSubstantiveUnresolvedExplanation(reasonText)) {
          continue;
        }
        return {
          reason: reasonText,
          fieldPath: `recommendations[${index}].description`,
          citations: numberedCitations(reasonText),
          nextStep: 'Complete buildable-envelope and utility engineering verification before fixing a recommended screening size range.',
        };
      }
      if (RECOMMENDED_RANGE_LABEL.test(withoutCitations(title)) && /\b(?:unresolved|unverified|indeterminate|unknown|pending|no recommended)\b/i.test(desc)) {
        const reasonText = recommendationQualification(row);
        if (!isSubstantiveUnresolvedExplanation(desc) && !isSubstantiveUnresolvedExplanation(reasonText)) {
          continue;
        }
        return {
          reason: reasonText,
          fieldPath: `recommendations[${index}].description`,
          citations: numberedCitations(reasonText),
          nextStep: 'Complete buildable-envelope and utility engineering verification before fixing a recommended screening size range.',
        };
      }
    }
    try {
      const campus = readCampusResourceAdvisory(report);
      if (campus && campus.result.status !== 'conditional-comparison') {
        const reasonText = `Sizing is unresolved in the saved campus-resource screening comparison (status: ${campus.result.status}; engineering verdict: ${campus.result.engineeringVerdict}). ${campus.result.conditions.join(' ')}`;
        return {
          reason: reasonText,
          fieldPath: 'siteDiligenceEvidence.campusResourceAdvisory',
          citations: [],
          nextStep: 'Verify net usable buildable acreage and utility deliverability before establishing a recommended screening size range.',
        };
      }
    } catch {
      // Malformed advisory does not supply an unresolved reason; caller handles integrity.
    }
    const maxCap = text(report.maxCampusCapacity);
    if (maxCap && isSubstantiveUnresolvedExplanation(maxCap) && !/^Gross area suggests\b/i.test(maxCap)) {
      return {
        reason: maxCap,
        fieldPath: 'maxCampusCapacity',
        citations: numberedCitations(maxCap),
        nextStep: 'Obtain a surveyed usable envelope and utility capacity confirmation before fixing campus sizing.',
      };
    }
  }
  if (id === 'cooling-strategy' || id === 'cooling-type') {
    for (const [index, row] of recommendations.entries()) {
      if (row.type !== 'cooling') continue;
      const arch = text(row.architecture);
      const desc = text(row.description);
      const qual = recommendationQualification(row);
      const candidate = isSubstantiveUnresolvedExplanation(desc)
        ? (qual || desc)
        : isSubstantiveUnresolvedExplanation(qual)
          ? qual
          : isSubstantiveUnresolvedExplanation(arch)
            ? arch
            : '';
      if (!candidate) continue;
      return {
        reason: candidate,
        fieldPath: `recommendations[${index}].description`,
        citations: numberedCitations(candidate),
        nextStep: 'Confirm workload heat density and cooling water/ambient constraints before selecting a cooling architecture.',
      };
    }
  }
  if (id === 'btm-architecture' || id === 'power-type') {
    for (const [index, row] of recommendations.entries()) {
      if (row.type !== 'power') continue;
      const arch = text(row.architecture);
      const desc = text(row.description);
      const qual = recommendationQualification(row);
      const candidate = isSubstantiveUnresolvedExplanation(desc)
        ? (qual || desc)
        : isSubstantiveUnresolvedExplanation(qual)
          ? qual
          : isSubstantiveUnresolvedExplanation(arch)
            ? arch
            : '';
      if (!candidate) continue;
      return {
        reason: candidate,
        fieldPath: `recommendations[${index}].description`,
        citations: numberedCitations(candidate),
        nextStep: 'Confirm utility service availability and on-site generation constraints before selecting a power architecture.',
      };
    }
  }
  return null;
}

/**
 * Display projection for an ALREADY USABLE immutable feasibility body. It reads
 * recommendation fields, not arbitrary summary prose, scores or a live Site.
 * Caller retains owner/freshness/loading gates and recorded-choice precedence.
 * Unknown/reset Intent is intentionally absent from these inputs. This helper
 * never builds a fleet, infers acreage capacity, adopts a choice or solves a
 * historical model. A qualified max-power bound is not a recommended range.
 */
export function deriveScreeningArchitectureRecommendations(
  report: ScreeningRecommendationReport | null | undefined,
  siteId?: string,
): ScreeningArchitectureRecommendations {
  if (!report || !sourceCanSupplyAdvisory(report, siteId)) return {};
  const result: ScreeningArchitectureRecommendations = {};
  const persistedSet = report.screeningRecommendationSetV1;
  if (persistedSet?.schemaVersion === 'screening-recommendation-set-v1' && persistedSet.questions) {
    for (const id of SCREENING_ARCHITECTURE_LINE_IDS) {
      const q = persistedSet.questions[id];
      if (q && (q.status === 'recommended' || q.status === 'alternatives') && hasAdvisoryValue(q.value)) {
        result[id] = {
          value: q.value!,
          basisNote: q.basisNote,
          reportId: q.reportId || report.id,
          fieldPath: q.fieldPath,
          citations: q.citations ?? [],
          ...(q.strategy ? { strategy: q.strategy } : {}),
          ...(q.islanded ? { islanded: q.islanded } : {}),
          ...(q.supplyOptions ? { supplyOptions: q.supplyOptions } : {}),
          ...(q.coolingStrategy ? { coolingStrategy: q.coolingStrategy } : {}),
          ...(q.heatRejection ? { heatRejection: q.heatRejection } : {}),
          ...(q.spoken ? { spoken: q.spoken } : {}),
        };
      }
    }
  }
  const recommendations = report.recommendations ?? [];
  const powerRows = recommendations.map((row, index) => ({ row, index })).filter(({ row }) => row.type === 'power');
  const primary = (report.btmDetailedAnalysis ?? []).map((row, index) => ({ row, index }))
    .filter(({ row }) => row.viabilityTier === 'primary' && hasAdvisoryValue(row.technology) && text(row.recommendation)
      && !/\b(?:not recommended|do not (?:use|select|adopt)|disqualified|not applicable)\b/i.test(row.recommendation));
  const alternative = (report.btmDetailedAnalysis ?? []).map((row, index) => ({ row, index }))
    .filter(({ row }) => row.viabilityTier === 'alternative' && hasAdvisoryValue(row.technology) && text(row.recommendation)
      && !/\b(?:not recommended|do not (?:use|select|adopt)|disqualified|not applicable)\b/i.test(row.recommendation));
  const hasSelectedPlanning = report.gridLedStandbyPlanningAtGen != null || report.gridLedStandbyPlanningProjection != null;

  // An active planning projection makes earlier prime-generation authority
  // inactive. The caller's selected-profile path supplies its recorded lines.
  if (!hasSelectedPlanning) {
    const architecture = alternatives(report, powerRows
      .filter(({ row }) => hasAdvisoryValue(row.architecture) && !RECOMMENDED_RANGE_LABEL.test(withoutCitations(text(row.title))))
      .map(({ row, index }) => savedLine(report, text(row.architecture),
        `recommendations[${index}].architecture`, recommendationQualification(row), supplySemantics(text(row.architecture)))));
    if (architecture) result['btm-architecture'] ??= architecture;
    const selected = primary.length ? primary : alternative;
    if (selected.length) {
      const value = selected.map(({ row }) => text(row.technology)).join('; ');
      const qualification = selected.map(({ row }) => [text(row.recommendation), text(row.intentAlignment)].filter(Boolean).join(' ')).join(' ');
      const semantic: Partial<ScreeningArchitectureRecommendation> = selected.length === 1 ? supplySemantics(text(selected[0]!.row.technology)) : {};
      result['power-type'] ??= savedLine(report,
        primary.length ? value : `Screening alternatives: ${value}`,
        `btmDetailedAnalysis[${selected.map(({ index }) => index).join(',')}].recommendation`, qualification, semantic);
      result['btm-architecture'] ??= savedLine(report,
        semantic.supplyOptions?.length === 1 && semantic.supplyOptions[0] === 'grid'
          ? `${value}; connection capacity unconfirmed` : `BTM-led supply: ${value}; grid connection unconfirmed`,
        `btmDetailedAnalysis[${selected.map(({ index }) => index).join(',')}].recommendation`, qualification, semantic);
    } else {
      const pathway = (report.developmentPathway ?? []).map((step, index) => ({ step, index }))
        .filter(({ step }) => hasAdvisoryValue(step.powerSource));
      if (pathway.length) {
        result['power-type'] ??= savedLine(report,
          pathway.map(({ step }) => `${text(step.phase)}: ${text(step.powerSource)}`).join('; '),
          `developmentPathway[${pathway.map(({ index }) => index).join(',')}].powerSource`,
          pathway
            .map(({ step }) =>
              text(step.description)
                .replace(/\n\nModeled phase water screening[\s\S]*$/i, '')
                .trim(),
            )
            .filter(Boolean)
            .join(' '));
      } else if (architecture?.supplyOptions?.length) {
        result['power-type'] ??= architecture;
      }
    }
    if (!result['btm-architecture']) {
      const adaptedPower = adaptProducerPowerArchitecture(report, powerRows);
      if (adaptedPower) {
        result['btm-architecture'] = adaptedPower;
        if (adaptedPower.supplyOptions?.length) {
          result['power-type'] ??= adaptedPower;
        }
      }
    }
  }

  const rangeRows = recommendations.map((row, index) => ({ row, index, value: row.type === 'power' || row.type === 'site' ? explicitSizeRange(row) : null }))
    .filter((candidate): candidate is { row: Recommendation; index: number; value: string } => candidate.value != null);
  // Two conflicting ranges are alternatives to investigate, not one chosen size;
  // identical duplicate ranges are deduplicated while retaining all conditions.
  const sizeRange = alternatives(
    report,
    rangeRows.map(candidate =>
      savedLine(report, candidate.value, `recommendations[${candidate.index}].description`, recommendationQualification(candidate.row)),
    ),
  );
  if (sizeRange) result['dc-power'] ??= sizeRange;

  const loops: ScreeningArchitectureRecommendation[] = [];
  const sinks: ScreeningArchitectureRecommendation[] = [];
  for (const [index, row] of recommendations.entries()) {
    if (row.type !== 'cooling') continue;
    const value = text(row.architecture) || text(row.description);
    if (!hasAdvisoryValue(value)) continue;
    const closedSemantic = coolingSemantics(value);
    const loopTitle = /^(?:rack cooling|rack[- ]cooling strategy|cooling loop|cooling strategy)$/i.test(text(row.title));
    const sinkTitle = /^(?:heat rejection|cold end|heat[- ]rejection strategy)$/i.test(text(row.title));
    const path = `recommendations[${index}].${text(row.architecture) ? 'architecture' : 'description'}`;
    if (closedSemantic.coolingStrategy || loopTitle || closedSemantic.heatRejection || sinkTitle) {
      const line = savedLine(report, value, path, recommendationQualification(row), closedSemantic);
      if (closedSemantic.coolingStrategy || loopTitle) loops.push(line);
      if (closedSemantic.heatRejection || sinkTitle) sinks.push(line);
    } else {
      const adaptedSemantic = adaptProducerCoolingSemantics(row);
      if (adaptedSemantic.coolingStrategy || adaptedSemantic.heatRejection) {
        const line = savedLine(report, value, path, recommendationQualification(row), adaptedSemantic);
        if (adaptedSemantic.coolingStrategy) loops.push(line);
        if (adaptedSemantic.heatRejection) sinks.push(line);
      }
    }
  }
  const loop = alternatives(report, loops);
  const sink = alternatives(report, sinks);
  if (loop) result['cooling-strategy'] ??= loop;
  if (sink) result['cooling-type'] ??= sink;
  const campus = readCampusResourceAdvisory(report);
  if (campus && !hasSelectedPlanning) {
    const fallback = campusResourceAdvisoryRecommendations(campus, report);
    for (const id of Object.keys(fallback) as ScreeningArchitectureLineId[]) result[id] ??= fallback[id];
  }
  for (const id of Object.keys(result) as ScreeningArchitectureLineId[]) {
    const line = result[id];
    if (line) result[id] = { ...line, spoken: line.spoken ?? compactSpoken(id, line) };
  }
  return result;
}

/**
 * Build the structured `ScreeningRecommendationSetV1` contract across all five
 * screening architecture questions, distinguishing `recommended`, `alternatives`,
 * `unresolved` (with explicit saved reason), `withheld-source`, and `not-produced`.
 */
export function deriveScreeningRecommendationSetV1(
  report: ScreeningRecommendationReport | null | undefined,
  siteId?: string,
  presentedAdvice?: ScreeningArchitectureRecommendations,
): ScreeningRecommendationSetV1 {
  const sourceDiag = diagnoseScreeningSourceState(report, siteId);
  const rawAdvice = sourceDiag.state === 'ready' ? deriveScreeningArchitectureRecommendations(report, siteId) : {};
  const effectiveAdvice = presentedAdvice ?? rawAdvice;
  const mode = report?.projectIntentAtGen?.screeningMode === 'evaluate-proposal' ? 'evaluate-proposal' : 'recommend';
  const reportId = report?.id ?? '';

  const questions = {} as Record<ScreeningArchitectureLineId, ScreeningQuestionAssessmentV1>;
  for (const id of SCREENING_ARCHITECTURE_LINE_IDS) {
    const line = effectiveAdvice[id];
    const rawLine = rawAdvice[id];
    if (line) {
      const isAlt = line.value.startsWith('Screening alternatives:');
      questions[id] = {
        questionId: id,
        status: isAlt ? 'alternatives' : 'recommended',
        value: line.value,
        ...(isAlt ? { alternatives: line.value.replace(/^Screening alternatives:\s*/i, '').split(/;\s*/).filter(Boolean) } : {}),
        basisNote: line.basisNote,
        reportId: line.reportId,
        fieldPath: line.fieldPath,
        citations: line.citations,
        ...(line.strategy ? { strategy: line.strategy } : {}),
        ...(line.islanded ? { islanded: line.islanded } : {}),
        ...(line.supplyOptions ? { supplyOptions: line.supplyOptions } : {}),
        ...(line.coolingStrategy ? { coolingStrategy: line.coolingStrategy } : {}),
        ...(line.heatRejection ? { heatRejection: line.heatRejection } : {}),
        ...(line.spoken ? { spoken: line.spoken } : {}),
      };
      continue;
    }
    if (rawLine && presentedAdvice && !presentedAdvice[id]) {
      questions[id] = {
        questionId: id,
        status: 'withheld-source',
        basisNote: 'Saved recommendation withheld because a cited source is unavailable.',
        unresolvedReason: 'Saved recommendation withheld because a cited source is unavailable.',
        nextStep: 'Verify or restore the cited source evidence and refresh Screening Assessment.',
        reportId,
        fieldPath: rawLine.fieldPath,
        citations: [],
      };
      continue;
    }
    const unresolved = sourceDiag.state === 'ready' ? deriveUnresolvedScreeningReason(report, id) : null;
    if (unresolved) {
      questions[id] = {
        questionId: id,
        status: 'unresolved',
        basisNote: unresolved.reason,
        unresolvedReason: unresolved.reason,
        ...(unresolved.nextStep ? { nextStep: unresolved.nextStep } : {}),
        reportId,
        fieldPath: unresolved.fieldPath,
        citations: unresolved.citations,
      };
      continue;
    }
    questions[id] = {
      questionId: id,
      status: 'not-produced',
      basisNote: sourceDiag.reason ?? 'No usable saved screening recommendation was produced for this question in this revision.',
      ...(sourceDiag.reason ? { unresolvedReason: sourceDiag.reason } : {}),
      nextStep: 'Review or refresh Screening Assessment. Recorded inputs remain comparison constraints, not a recommended approach.',
      reportId,
      fieldPath: '',
      citations: [],
    };
  }

  return {
    schemaVersion: 'screening-recommendation-set-v1',
    reportId,
    screeningMode: mode,
    sourceState: sourceDiag.state,
    ...(sourceDiag.reason ? { sourceReason: sourceDiag.reason } : {}),
    questions,
  };
}
